import React, { useEffect, useMemo, useState } from "react";
import { fetchRegions, fetchAgronomyCrops, fetchSowingPlan } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";
import DualExplainer from "@/components/DualExplainer";
import { CalendarBlank, Plant, CircleNotch, Sun, Drop, CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StoryCard } from "@/components/Explain";

const LABEL_COLOR = {
  ideal: "#00FA9A", gut: "#6B8E5A", möglich: "#FFC24B",
  schlecht: "#FF5500", "nicht geeignet": "#A1A1AA",
};

export default function Planner() {
  const [regions, setRegions] = useState([]);
  const [crops, setCrops] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [cropId, setCropId] = useState("winterweizen");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRegions().then((r) => { setRegions(r); if (r[0]) setRegionId(r[0].id); }).catch(() => {});
    fetchAgronomyCrops().then((d) => setCrops(d.crops || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!regionId || !cropId) return;
    setLoading(true); setPlan(null);
    fetchSowingPlan(regionId, cropId)
      .then(setPlan)
      .catch(() => toast.error("Planer konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [regionId, cropId]);

  const crop = useMemo(() => crops.find((c) => c.id === cropId), [crops, cropId]);

  return (
    <div className="space-y-6" data-testid="planner-root">
      <header>
        <div className="eid-label flex items-center gap-1.5"><CalendarBlank size={11} /> Saattermin-Rechner · 21-Tage-Ausblick</div>
        <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tighter mt-1 leading-[0.95]">Ernteplaner</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl leading-relaxed">
          Der Planer zeigt dir für jeden der nächsten 21 Tage, ob Bodentemperatur &amp;
          Bodenfeuchte zum Säen passen. So findest du das ideale 2–3-Tage-Fenster.
        </p>
      </header>

      <StoryCard
        eyebrow="Was macht der Ernteplaner?"
        title="Drei Tage finden, an denen die Saat aufgehen wird."
        lead="Wähle Region + Kultur. Bodenblick rechnet für jeden der nächsten 21 Tage aus, wie geeignet er für deine Aussaat ist — und zeigt dir die 2–3 grünsten Tage. So sparst du Saatgut, Diesel und Nerven."
        bullets={[
          "Grün = ideal · Gelb = möglich, aber Aufwand · Rot = besser warten",
          "Berücksichtigt: Bodentemperatur, Bodenfeuchte, Kultur-typische Saat-Fenster",
          "Quelle: ICON-Modell + ERA5 — die gleichen Daten, die der DWD selbst nutzt",
        ]}
        accent="#00FA9A"
        icon={Sparkle}
        testid="planner-story"
      />

      <DualExplainer
        title="Wie berechnet sich ein guter Saattag?"
        testid="planner-explainer"
        simple={
          <>
            <p>Wir kombinieren 3 Faktoren für jeden Tag:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Bodentemperatur</strong> (aus Lufttemperatur 5-Tage-Mittel)</li>
              <li><strong>Bodenfeuchte</strong> (weder zu trocken noch zu nass)</li>
              <li><strong>Monat &amp; Kultur</strong> — Winterweizen säst du nicht im April</li>
            </ul>
            <p className="text-[#00FA9A]">
              <strong>Praxis-Tipp:</strong> Nimm den <strong>grünsten Tag innerhalb deiner Wunschwoche</strong> —
              musst ihn aber nicht erzwingen, wenn die Feldbefahrbarkeit nicht passt.
            </p>
          </>
        }
        science={
          <>
            <p>
              <strong>Heuristik:</strong> Score = 100 − Strafpunkte. Strafe bei T &lt; t_min (linear
              15 Punkte/°C), kühl-aber-über-min (−20), übertemperiert (−25), Bodenfeuchte-Stress
              (&lt;0.16: −30; &lt;0.20: −10; &gt;0.38: −25). Monat außerhalb Saatzeit → Score = 0.
            </p>
            <p>
              <strong>Datenlage:</strong> Historisch 7 Tage aus ERA5-Land; Forward 14 Tage als
              Persistenz-Prognose (letzter 5-Tage-Mittelwert). Für Präzisions-Landwirtschaft:
              ICON-D2 EPS oder ECMWF-HRES/SEAS5 empfohlen. Bodentemp wird via
              Lufttemperatur approximiert — Realwert schwankt je nach Beschattung/Textur ±2 °C.
            </p>
            <p>
              <strong>Parameter-Matrix:</strong> 10 Kulturen mit (soil_temp_min, opt_min, opt_max,
              window_month) aus Literatur (Deutsche Landwirtschafts-Gesellschaft DLG-Empfehlungen,
              LfL Bayern Saatzeitkalender, AGES Saatgut-Standards AT).
            </p>
          </>
        }
      />

      <section className="grid grid-cols-1 md:grid-cols-[1.3fr,1fr] gap-3 items-end" data-testid="planner-controls">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Region</div>
          <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="planner-region-select" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Kultur</div>
          <select
            value={cropId}
            onChange={(e) => setCropId(e.target.value)}
            data-testid="planner-crop-select"
            className="w-full bg-[#121212] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#00FA9A]/40"
          >
            {crops.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      </section>

      {crop && (
        <div className="eid-card p-3 text-xs text-zinc-400 flex items-start gap-2" data-testid="planner-crop-info">
          <Plant size={14} className="shrink-0 mt-0.5 text-[#00FA9A]" />
          <span>
            <strong className="text-white">{crop.label}:</strong> {crop.bbch} ·
            Keimung ab {crop.soil_temp_min} °C · Optimum {crop.opt_min}–{crop.opt_max} °C ·
            Saatzeit Monate {crop.window_month.join("/")}
          </span>
        </div>
      )}

      {loading && (
        <div className="eid-card p-8 flex items-center justify-center gap-2 text-zinc-400" data-testid="planner-loading">
          <CircleNotch size={18} className="animate-spin" /> Lade Prognose…
        </div>
      )}

      {plan && <PlanGrid plan={plan} />}
    </div>
  );
}

function PlanGrid({ plan }) {
  const best = plan.best_day;
  return (
    <section className="space-y-3" data-testid="planner-plan">
      {best && best.score >= 60 && (
        <div className="eid-card p-4 border-l-4 border-[#00FA9A]/40 bg-[#00FA9A]/10 flex items-start gap-3" data-testid="planner-best">
          <CheckCircle size={22} weight="fill" className="text-[#00FA9A] shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#00FA9A]">Empfohlener Saattag</div>
            <div className="text-lg font-medium tracking-tight">
              {new Date(best.date).toLocaleDateString("de-AT", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              Score {best.score}/100 · {best.t_mean} °C · Feuchte {best.soil_moisture}
            </div>
          </div>
        </div>
      )}

      <div className="eid-card p-4">
        <div className="eid-label mb-2 flex items-center gap-1.5"><CalendarBlank size={11} /> 21 Tage im Blick</div>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-1.5" data-testid="planner-day-grid">
          {plan.days.map((d) => (
            <DayTile key={d.date} day={d} />
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: LABEL_COLOR.ideal }} /> Ideal</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: LABEL_COLOR.gut }} /> Gut</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: LABEL_COLOR.möglich }} /> Möglich</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: LABEL_COLOR.schlecht }} /> Schlecht</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: LABEL_COLOR["nicht geeignet"] }} /> Ungeeignet</span>
        </div>
      </div>

      <div className="text-xs text-zinc-400 border-l-2 border-[#FFD700]/40 pl-3">{plan.disclaimer}</div>
    </section>
  );
}

function DayTile({ day }) {
  const bg = LABEL_COLOR[day.label] || "#8A8A93";
  const dt = new Date(day.date);
  return (
    <div
      className="rounded-sm p-2 text-white text-xs relative group"
      style={{ background: bg }}
      data-testid={`planner-day-${day.date}`}
      title={day.reasons.join(" · ")}
    >
      <div className="font-mono text-[10px] opacity-90">
        {dt.toLocaleDateString("de-AT", { weekday: "short" })}
      </div>
      <div className="font-medium">
        {dt.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" })}
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] opacity-90">
        <span className="inline-flex items-center gap-0.5"><Sun size={9} /> {day.t_mean ?? "–"}°</span>
        <span className="inline-flex items-center gap-0.5"><Drop size={9} /> {day.soil_moisture ?? "–"}</span>
      </div>
      <div className="text-[9px] uppercase font-mono mt-0.5 opacity-80">
        {day.source === "hist" ? "Messwert" : "Prognose"} · {day.score}
      </div>
    </div>
  );
}
